// ─── Shared styles — earthy orange palette (matches series) ──────────────────
const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid #7a2e00', fontFamily:"'Georgia',serif", color:'#1a0e06', lineHeight:'1.7', background:'#fdf6ee' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c45a10', paddingBottom:'8px', marginBottom:'24px', color:'#7a2e00' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#a04010', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c45a10', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'1rem' },
  infoBox:      { background:'#fdf0e4', border:'2px solid #c45a10', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New',monospace", fontSize:'0.92rem', color:'#7a2e00', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.95rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c45a10' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.95rem' },
  th:           { background:'#a04010', color:'#fff', padding:'11px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.93rem' },
  tdE:          { padding:'11px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf4ec' },
  tdO:          { padding:'11px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf6ee' },
  tdL:          { fontWeight:'600', color:'#7a2e00', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New',monospace", fontSize:'0.92rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fdf4ec', border:'1px solid #e8b07a', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'1rem', color:'#7a2e00', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.88rem', color:'#7a4a28', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((it,i)=><div key={i} style={S.infoBoxItem}>{it}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{...S.th,width:'220px'}}>Concept</th>
        <th style={S.th}>Detail</th>
      </tr></thead>
      <tbody>{rows.map(([l,v],i)=>(
        <tr key={i}>
          <td style={{...(i%2===0?S.tdE:S.tdO),...S.tdL}}>{l}</td>
          <td style={{...(i%2===0?S.tdE:S.tdO),...S.code}}>{v}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

// ─── SVG helpers ──────────────────────────────────────────────────────────────
function arrow(id, color='#c45a10') {
  return <marker id={id} markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
    <polygon points="0 0,7 4,0 8" fill={color}/>
  </marker>;
}
function arrowBack(id, color='#c45a10') {
  return <marker id={id} markerWidth="8" markerHeight="8" refX="2" refY="4" orient="auto">
    <polygon points="7 0,0 4,7 8" fill={color}/>
  </marker>;
}

// Draw a stepper motor icon centred at (cx,cy) — returns SVG group element
function motorIcon(cx, cy, r, label, color) {
  return (
    <g key={`motor-${cx}-${cy}`}>
      <rect x={cx-r} y={cy-r} width={r*2} height={r*2} rx={4}
        fill={color} stroke="#7a2e00" strokeWidth="2"/>
      <rect x={cx+r} y={cy-5} width={10} height={10} rx={2}
        fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
      <text x={cx} y={cy+5} textAnchor="middle" fontSize="13" fontWeight="700" fill="#fdf4ec">{label}</text>
    </g>
  );
}

// Draw a pulley — returns SVG group element
function pulley(cx, cy, r, color) {
  return (
    <g key={`pulley-${cx}-${cy}`}>
      <circle cx={cx} cy={cy} r={r} fill={color} stroke="#7a2e00" strokeWidth="1.8"/>
      <circle cx={cx} cy={cy} r={r*0.4} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.2"/>
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 1 — CoreXY full anatomy with belt path and kinematics labels
// ─────────────────────────────────────────────────────────────────────────────
function DiagramAnatomy() {
  const W=1100, H=500;

  // Frame — shifted right enough for motors, left enough for right panel
  const fx=110, fy=50, fw=450, fh=370;
  const cx=fx+fw/2, cy=fy+fh/2;
  const cr=18;

  const mAx=fx-62, mAy=fy+55;
  const mBx=fx-62, mBy=fy+fh-55;

  const pulleys = [
    {id:'TL', x:fx,    y:fy    },
    {id:'TR', x:fx+fw, y:fy    },
    {id:'BL', x:fx,    y:fy+fh },
    {id:'BR', x:fx+fw, y:fy+fh },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>CoreXY — Full Anatomy: Motors · Pulleys · Dual Belt Paths · Carriage · Frame</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          {arrow('arAN')}
          {arrowBack('arANb')}
          <marker id="arANd" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <polygon points="0 0,7 4,0 8" fill="#7a2e00"/>
          </marker>
        </defs>

        {/* ── Frame ── */}
        <rect x={fx} y={fy} width={fw} height={fh} rx={6}
          fill="none" stroke="#7a2e00" strokeWidth="3"/>
        {[0,1,2,3,4,5].map(i=>(
          <g key={i}>
            <line x1={fx-8} y1={fy+60+i*48} x2={fx+8} y2={fy+52+i*48} stroke="#7a2e00" strokeWidth="1.5"/>
            <line x1={fx+fw-8} y1={fy+60+i*48} x2={fx+fw+8} y2={fy+52+i*48} stroke="#7a2e00" strokeWidth="1.5"/>
          </g>
        ))}

        {/* ── X-axis rails ── */}
        {[fy+fh*0.3, fy+fh*0.7].map((ry,i)=>(
          <rect key={i} x={fx+2} y={ry-4} width={fw-4} height={8} rx={3}
            fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5" opacity="0.7"/>
        ))}
        {/* X-rail label — placed left of frame so it stays in viewbox */}
        <line x1={fx} y1={fy+fh*0.3} x2={fx-36} y2={fy+fh*0.3-20} stroke="#7a2e00" strokeWidth="1.2"/>
        <text x={fx-38} y={fy+fh*0.3-25} textAnchor="end" fontSize="10" fill="#7a2e00" fontWeight="700">X rails</text>
        <text x={fx-38} y={fy+fh*0.3-11} textAnchor="end" fontSize="9"  fill="#a04010">(linear guides)</text>

        {/* ── Carriage ── */}
        <rect x={cx-cr} y={cy-cr} width={cr*2} height={cr*2} rx={5}
          fill="#c45a10" stroke="#7a2e00" strokeWidth="2.5"/>
        <text x={cx} y={cy+5} textAnchor="middle" fontSize="10" fontWeight="700" fill="#fdf4ec">HEAD</text>
        {[fy+fh*0.3, fy+fh*0.7].map((ry,i)=>(
          <rect key={i} x={cx-cr-4} y={ry-6} width={8} height={12} rx={2}
            fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
        ))}

        {/* ── Corner pulleys ── */}
        {pulleys.map(p=> pulley(p.x, p.y, 11, '#d4874a') )}

        {/* ── Belt A ── */}
        <line x1={mAx+44} y1={mAy} x2={fx} y2={fy} stroke="#c45a10" strokeWidth="3" opacity="0.85"/>
        <line x1={fx} y1={fy} x2={cx-cr} y2={cy-8} stroke="#c45a10" strokeWidth="3" opacity="0.85"/>
        <line x1={cx+cr} y1={cy-8} x2={fx+fw} y2={fy} stroke="#c45a10" strokeWidth="3" opacity="0.85"/>
        <line x1={fx+fw} y1={fy} x2={fx+fw} y2={fy+fh} stroke="#c45a10" strokeWidth="1.5" strokeDasharray="6,4" opacity="0.5"/>
        <line x1={fx+fw} y1={fy+fh} x2={mAx+44} y2={mAy} stroke="#c45a10" strokeWidth="1.5" strokeDasharray="6,4" opacity="0.5"/>

        {/* ── Belt B ── */}
        <line x1={mBx+44} y1={mBy} x2={fx} y2={fy+fh} stroke="#7a2e00" strokeWidth="3" opacity="0.85"/>
        <line x1={fx} y1={fy+fh} x2={cx-cr} y2={cy+8} stroke="#7a2e00" strokeWidth="3" opacity="0.85"/>
        <line x1={cx+cr} y1={cy+8} x2={fx+fw} y2={fy+fh} stroke="#7a2e00" strokeWidth="3" opacity="0.85"/>
        <line x1={fx+fw} y1={fy+fh} x2={fx+fw} y2={fy} stroke="#7a2e00" strokeWidth="1.5" strokeDasharray="6,4" opacity="0.5"/>
        <line x1={fx+fw} y1={fy} x2={mBx+44} y2={mBy} stroke="#7a2e00" strokeWidth="1.5" strokeDasharray="6,4" opacity="0.5"/>

        {/* ── Motors ── */}
        {motorIcon(mAx, mAy, 22, 'A', '#c45a10')}
        {motorIcon(mBx, mBy, 22, 'B', '#7a2e00')}
        <text x={mAx} y={mAy-28} textAnchor="middle" fontSize="10" fontWeight="700" fill="#c45a10">Motor A</text>
        <text x={mBx} y={mBy+38} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Motor B</text>

        {/* ── Motion arrows ── */}
        <line x1={cx+28} y1={cy} x2={cx+64} y2={cy}
          stroke="#c45a10" strokeWidth="2.5" markerEnd="url(#arAN)"/>
        <text x={cx+70} y={cy+4} fontSize="10" fontWeight="700" fill="#c45a10">+X</text>
        <line x1={cx} y1={cy+28} x2={cx} y2={cy+64}
          stroke="#a04010" strokeWidth="2.5" markerEnd="url(#arAN)"/>
        <text x={cx+6} y={cy+74} fontSize="10" fontWeight="700" fill="#a04010">+Y</text>

        {/* ── Right info panel: x=610, width=480 → right edge=1090 < W=1100 ── */}
        <rect x={612} y={42} width={478} height={410} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={851} y={64} textAnchor="middle" fontSize="12" fontWeight="700" fill="#7a2e00">Kinematics Summary</text>
        <line x1={620} y1={72} x2={1082} y2={72} stroke="#e8b07a"/>
        {[
          ['Motion type:',    'Cartesian 2-axis (X and Y)'],
          ['',                'Both motors stationary on frame'],
          ['',                ''],
          ['Belt A drives:',  'Δx  and  +Δy  simultaneously'],
          ['Belt B drives:',  'Δx  and  −Δy  simultaneously'],
          ['',                ''],
          ['X movement:',     'Both motors same direction'],
          ['',                'X = (A + B) / 2'],
          ['Y movement:',     'Motors opposite directions'],
          ['',                'Y = (A − B) / 2'],
          ['',                ''],
          ['Carriage:',       'Toolhead — carries print nozzle,'],
          ['',                'laser, pen, or probe'],
          ['',                ''],
          ['Frame fixed:',    'Motors, pulleys all stationary'],
          ['',                'Only carriage moves → low inertia'],
          ['',                ''],
          ['Belt type:',       'GT2 (2 mm pitch) standard'],
          ['',                 'GT3 (3 mm) for higher torque'],
          ['',                 '6 mm or 9 mm belt width'],
          ['',                ''],
          ['Typical speed:',  '100–500 mm/s (3D printing)'],
          ['',                '1000+ mm/s (laser, tuned)'],
          ['',                ''],
          ['Accuracy:',       '0.01–0.05 mm typical resolution'],
          ['',                'Limited by belt stretch & backlash'],
        ].map(([l,v],i)=>(
          <g key={i}>
            <text x={622} y={84+i*14.5} fontSize="10" fontWeight={l?'700':'400'} fill="#7a2e00">{l}</text>
            <text x={l?748:628} y={84+i*14.5} fontSize="10" fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}

        {/* Belt legend — inside panel, well above bottom */}
        <line x1={620} y1={452} x2={658} y2={452} stroke="#c45a10" strokeWidth="3"/>
        <text x={664} y={456} fontSize="10" fontWeight="700" fill="#c45a10">Belt A (active segment)</text>
        <line x1={620} y1={470} x2={658} y2={470} stroke="#7a2e00" strokeWidth="3"/>
        <text x={664} y={474} fontSize="10" fontWeight="700" fill="#7a2e00">Belt B (active segment)</text>
      </svg>
      <div style={S.dCaption}>Both motors are fixed to the frame — the carriage carries only its own mass (toolhead + belt clips), keeping moving inertia minimal and enabling higher acceleration than bed-slinger designs</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Kinematic equations: 4 motion states with motor direction
// ─────────────────────────────────────────────────────────────────────────────
function DiagramKinematics() {
  const W=980, H=380;

  const cases = [
    { label:'+X (right)',    A:'+1', B:'+1', Ax:1,  Ay:0,  Bx:1,  By:0,  Hx:1,  Hy:0,  color:'#c45a10' },
    { label:'−X (left)',     A:'−1', B:'−1', Ax:-1, Ay:0,  Bx:-1, By:0,  Hx:-1, Hy:0,  color:'#8a1010' },
    { label:'+Y (forward)',  A:'+1', B:'−1', Ax:1,  Ay:0,  Bx:-1, By:0,  Hx:0,  Hy:1,  color:'#5a7820' },
    { label:'−Y (back)',     A:'−1', B:'+1', Ax:-1, Ay:0,  Bx:1,  By:0,  Hx:0,  Hy:-1, color:'#a04010' },
    { label:'+X+Y (diagonal)',A:'+1',B:'0',  Ax:1,  Ay:0,  Bx:0,  By:0,  Hx:0.7,Hy:0.7,color:'#c45a10' },
    { label:'+X−Y (diagonal)',A:'0', B:'+1', Ax:0,  Ay:0,  Bx:1,  By:0,  Hx:0.7,Hy:-0.7,color:'#7a2e00'},
  ];

  const colW=155, colH=240, startX=10, startY=50;

  function MiniFrame({cx, cy, hx, hy, color, A, B}) {
    const fr=52; // mini frame half-size
    const ax=cx, ay=cy; // carriage centre = frame centre
    const arrLen=34;
    // carriage
    const headX = ax + hx*fr*0.5;
    const headY = ay + hy*fr*0.5;
    return (
      <g>
        {/* Mini frame */}
        <rect x={ax-fr} y={ay-fr} width={fr*2} height={fr*2} rx={4}
          fill="none" stroke="#7a2e00" strokeWidth="2"/>
        {/* Carriage */}
        <circle cx={headX} cy={headY} r={9} fill={color} stroke="#7a2e00" strokeWidth="1.8"/>
        {/* Carriage motion arrow */}
        {(Math.abs(hx)+Math.abs(hy))>0 && (
          <line x1={headX} y1={headY}
            x2={headX+hx*24} y2={headY+hy*24}
            stroke={color} strokeWidth="2.5" markerEnd="url(#arKM)"/>
        )}
        {/* Motor A indicator */}
        <rect x={ax-fr-18} y={ay-fr-8} width={16} height={16} rx={3}
          fill={A!=='0'?(A==='+1'?'#c45a10':'#8a1010'):'#e8b07a'}
          stroke="#7a2e00" strokeWidth="1.2"/>
        <text x={ax-fr-10} y={ay-fr+5} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#fdf4ec">{A}</text>
        <text x={ax-fr-10} y={ay-fr-14} textAnchor="middle" fontSize="7.5" fill="#7a2e00">A</text>
        {/* Motor B indicator */}
        <rect x={ax-fr-18} y={ay+fr-8} width={16} height={16} rx={3}
          fill={B!=='0'?(B==='+1'?'#c45a10':'#8a1010'):'#e8b07a'}
          stroke="#7a2e00" strokeWidth="1.2"/>
        <text x={ax-fr-10} y={ay+fr+5} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#fdf4ec">{B}</text>
        <text x={ax-fr-10} y={ay+fr-14} textAnchor="middle" fontSize="7.5" fill="#7a2e00">B</text>
      </g>
    );
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>CoreXY Kinematics — Motor Direction vs. Carriage Motion for Six Motion States</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arKM" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <polygon points="0 0,7 4,0 8" fill="#c45a10"/>
          </marker>
        </defs>

        {cases.map((c,i)=>{
          const col = i % 6;
          const cx = startX + col*(colW) + colW/2 + 18;
          const cy = startY + 115;
          return (
            <g key={i}>
              {/* Background card */}
              <rect x={startX+col*colW+4} y={startY+2} width={colW-8} height={colH}
                rx={6} fill={i%2===0?'#fdf4ec':'#fdf0e4'} stroke="#e8b07a" strokeWidth="1.2"/>
              {/* Title */}
              <text x={cx} y={startY+22} textAnchor="middle" fontSize="11" fontWeight="700" fill={c.color}>
                {c.label}
              </text>
              <MiniFrame cx={cx} cy={cy} hx={c.Hx} hy={c.Hy} color={c.color} A={c.A} B={c.B}/>
              {/* Equations */}
              <text x={cx} y={cy+88} textAnchor="middle" fontSize="9.5" fontFamily="'Courier New',monospace" fill="#7a2e00">
                A = {c.A === '+1' ? '+step' : c.A === '−1' ? '−step' : 'stop'}
              </text>
              <text x={cx} y={cy+103} textAnchor="middle" fontSize="9.5" fontFamily="'Courier New',monospace" fill="#7a2e00">
                B = {c.B === '+1' ? '+step' : c.B === '−1' ? '−step' : 'stop'}
              </text>
              <line x1={cx-50} y1={cy+112} x2={cx+50} y2={cy+112} stroke="#e8b07a" strokeWidth="1"/>
              <text x={cx} y={cy+126} textAnchor="middle" fontSize="9" fill="#a04010">
                X = (A+B)/2  ·  Y = (A−B)/2
              </text>
            </g>
          );
        })}

        {/* Bottom equation bar */}
        <rect x={10} y={H-36} width={W-20} height={28} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.2"/>
        <text x={W/2} y={H-18} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">
          X_steps = (A+B)/2   ·   Y_steps = (A−B)/2   ·   A_steps = X+Y   ·   B_steps = X−Y
        </text>
      </svg>
      <div style={S.dCaption}>Diagonal motion (e.g. +X+Y simultaneously) requires only one motor to move — the other is stationary; this is unique to CoreXY and is a key difference from traditional Cartesian H-bot designs</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Belt path detail: crossing pattern, idler routing, clamp points
// ─────────────────────────────────────────────────────────────────────────────
function DiagramBeltPath() {
  const W=980, H=440;

  // Two sub-diagrams side by side: correct CoreXY vs wrong H-bot
  function CoreXYPath({ox, oy, w, h}) {
    // Frame
    const pr = 10; // pulley radius
    // Pulleys: TL, TR, BL, BR + 2 midpoint idlers each belt
    const TL=[ox,oy], TR=[ox+w,oy], BL=[ox,oy+h], BR=[ox+w,oy+h];
    // Carriage centre
    const cx=ox+w/2, cy=oy+h/2;
    const cr=14;

    // Belt A: orange — goes from motor (left wall, top) via TL → carriage → TR → (return via right wall and bottom)
    // Correctly: the TWO belts cross in the middle but are at different Z-heights (not physically interfering)
    const bApts = [
      [ox-40, oy+30],   // Motor A
      [ox, oy],          // TL pulley
      [cx-8, cy-10],    // carriage left attach (belt A)
    ];
    const bBpts = [
      [ox-40, oy+h-30], // Motor B
      [ox, oy+h],        // BL pulley
      [cx-8, cy+10],    // carriage left attach (belt B)
    ];
    // Right side of belts
    const bAptsr = [
      [cx+8, cy-10],    // carriage right attach (belt A)
      [ox+w, oy],        // TR pulley
      [ox+w, oy+h],      // BR pulley (return path)
    ];
    const bBptsr = [
      [cx+8, cy+10],    // carriage right attach (belt B)
      [ox+w, oy+h],      // BR pulley
      [ox+w, oy],        // TR pulley (return path, top)
    ];

    const toPath = pts => 'M ' + pts.map(p=>p.join(',')).join(' L ');

    return (
      <g>
        <rect x={ox} y={oy} width={w} height={h} rx={4} fill="none" stroke="#7a2e00" strokeWidth="2.5"/>
        {/* Rails */}
        {[oy+h*0.35, oy+h*0.65].map((ry,i)=>(
          <rect key={i} x={ox+4} y={ry-3} width={w-8} height={6} rx={2}
            fill="#e8b07a" stroke="#7a2e00" strokeWidth="1" opacity="0.7"/>
        ))}
        {/* Carriage */}
        <rect x={cx-cr} y={cy-cr} width={cr*2} height={cr*2} rx={4}
          fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={cx} y={cy+4} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fdf4ec">HEAD</text>

        {/* Belt A — orange */}
        <path d={toPath(bApts)} fill="none" stroke="#c45a10" strokeWidth="3.5" opacity="0.9"/>
        <path d={toPath(bAptsr)} fill="none" stroke="#c45a10" strokeWidth="3.5" opacity="0.9"/>
        {/* Belt B — dark brown */}
        <path d={toPath(bBpts)} fill="none" stroke="#7a2e00" strokeWidth="3.5" opacity="0.9"/>
        <path d={toPath(bBptsr)} fill="none" stroke="#7a2e00" strokeWidth="3.5" opacity="0.9"/>

        {/* Crossing zone marker — placed at the true crossing of the two diagonals:
            Belt A goes TL[ox,oy] → carriage-left[cx-8, cy-10]
            Belt B goes BL[ox,oy+h] → carriage-right[cx+8, cy+10]
            Parametric intersection gives a point in upper-left quadrant */}
        {(()=>{
          // Line 1: (ox,oy) → (cx-8, cy-10)
          const x1=ox, y1=oy, x2=cx-8, y2=cy-10;
          // Line 2: (ox,oy+h) → (cx+8, cy+10)
          const x3=ox, y3=oy+h, x4=cx+8, y4=cy+10;
          const denom=(x1-x2)*(y3-y4)-(y1-y2)*(x3-x4);
          const t=((x1-x3)*(y3-y4)-(y1-y3)*(x3-x4))/denom;
          // Intersection at t along line 1
          const ix=x1+t*(x2-x1), iy=y1+t*(y2-y1);
          // This will be near the carriage; shift up-left by 20% along the diagonal
          const mx=x1+(ix-x1)*0.55, my=y1+(iy-y1)*0.55;
          return <g>
            <circle cx={mx} cy={my} r={11}
              fill="#fdf0e4" stroke="#c45a10" strokeWidth="2.2" opacity="0.96"/>
            <text x={mx} y={my+4} textAnchor="middle"
              fontSize="10" fontWeight="700" fill="#c45a10">✕</text>
          </g>;
        })()}

        {/* Callout annotation — upper-left of frame, pointing to crossing */}
        <rect x={ox+22} y={oy+18} width={160} height={32} rx={3}
          fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.2"/>
        <text x={ox+28} y={oy+32} fontSize="9.5" fontWeight="700" fill="#7a2e00">Belts cross (different Z)</text>
        <text x={ox+28} y={oy+46} fontSize="9"   fill="#a04010">no physical contact</text>

        {/* Pulleys */}
        {[TL,TR,BL,BR].map(([px,py],i)=>(
          <g key={i}>
            <circle cx={px} cy={py} r={pr} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.8"/>
            <circle cx={px} cy={py} r={pr*0.4} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1"/>
          </g>
        ))}

        {/* Motors */}
        <rect x={ox-54} y={oy+10}    width={32} height={32} rx={3} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={ox-38} y={oy+30}    textAnchor="middle" fontSize="10" fontWeight="700" fill="#fdf4ec">A</text>
        <rect x={ox-54} y={oy+h-42}  width={32} height={32} rx={3} fill="#7a2e00" stroke="#5a1e00" strokeWidth="2"/>
        <text x={ox-38} y={oy+h-22}  textAnchor="middle" fontSize="10" fontWeight="700" fill="#fdf4ec">B</text>
      </g>
    );
  }

  // Idler detail diagram (right panel) — ox=540, panel right edge=980
  function IdlerDetail({ox, oy}) {
    const cx=ox+90, cy=oy+110;  // cx=630, within panel, labels right of cx reach ~780
    return (
      <g>
        <text x={ox+185} y={oy+14} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">
          Idler Pulley Detail
        </text>
        {/* Shaft */}
        <line x1={cx} y1={oy+22} x2={cx} y2={oy+210} stroke="#7a2e00" strokeWidth="3"/>
        {/* Pulley body */}
        <circle cx={cx} cy={cy} r={36} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2.5"/>
        <circle cx={cx} cy={cy} r={22} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
        <circle cx={cx} cy={cy} r={6}  fill="#7a2e00"/>
        {/* Belt over pulley */}
        <line x1={cx-36} y1={cy-5} x2={cx-70} y2={cy-5} stroke="#c45a10" strokeWidth="6" strokeLinecap="round"/>
        <line x1={cx+36} y1={cy-5} x2={cx+74} y2={cy-5} stroke="#c45a10" strokeWidth="6" strokeLinecap="round"/>
        {/* Belt teeth on right side */}
        {[0,1,2,3].map((ti,i)=>(
          <rect key={i} x={cx+36+ti*12} y={cy-9} width={8} height={4} rx={1}
            fill="#a04010" stroke="none" opacity="0.8"/>
        ))}
        {/* GT2 belt label — RIGHT side leader */}
        <line x1={cx+60} y1={cy-5} x2={cx+90} y2={cy+30} stroke="#7a2e00" strokeWidth="1.2"/>
        <text x={cx+94} y={cy+34} fontSize="9.5" fontWeight="700" fill="#7a2e00">GT2 belt</text>
        <text x={cx+94} y={cy+47} fontSize="9"   fill="#a04010">2 mm pitch, 6/9 mm wide</text>
        {/* Smooth idler label */}
        <line x1={cx} y1={cy+36} x2={cx+60} y2={cy+72} stroke="#7a2e00" strokeWidth="1.2"/>
        <text x={cx+64} y={cy+76} fontSize="9.5" fontWeight="700" fill="#7a2e00">Smooth idler</text>
        <text x={cx+64} y={cy+89} fontSize="9"   fill="#a04010">or toothed idler</text>
        {/* F695 bearing — left-anchored below pulley centre, within panel */}
        <line x1={cx-36} y1={cy} x2={cx-56} y2={cy+52} stroke="#7a2e00" strokeWidth="1.2"/>
        <text x={cx-60} y={cy+56} textAnchor="end" fontSize="9.5" fontWeight="700" fill="#7a2e00">F695 / F623</text>
        <text x={cx-60} y={cy+69} textAnchor="end" fontSize="9"   fill="#a04010">bearing or pulley</text>
      </g>
    );
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Belt Path Detail — CoreXY Dual Belt Routing, Crossing Zone, and Idler Pulley Construction</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <text x={230} y={28} textAnchor="middle" fontSize="12" fontWeight="700" fill="#7a2e00">CoreXY Belt Routing</text>
        <text x={730} y={28} textAnchor="middle" fontSize="12" fontWeight="700" fill="#7a2e00">Idler &amp; Pulley Construction</text>
        <line x1={510} y1={20} x2={510} y2={H-10} stroke="#e8b07a" strokeWidth="1" strokeDasharray="5,3"/>

        <CoreXYPath ox={80} oy={44} w={380} h={340}/>
        <IdlerDetail ox={540} oy={38}/>

        {/* Belt material note */}
        <rect x={82} y={H-38} width={376} height={28} rx={4} fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1"/>
        <text x={270} y={H-20} textAnchor="middle" fontSize="9.5" fill="#7a2e00" fontWeight="700">
          The two belts cross at the frame interior but run at different heights — no physical contact
        </text>
      </svg>
      <div style={S.dCaption}>Belt path geometry is critical: the two belts must be routed so they cross in 3D space without touching — typically one belt runs on top-mounted idlers and the other on bottom-mounted idlers</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 4 — CoreXY vs H-Bot vs Cartesian comparison
// ─────────────────────────────────────────────────────────────────────────────
function DiagramComparison() {
  const W=980, H=320;

  // Three mini schematics + comparison bars
  const systems = [
    { name:'CoreXY',       x:80,  color:'#c45a10',
      pros:['Stationary motors → low inertia','High speed (500+ mm/s)','Equal X/Y resolution','No belt racking issue'],
      cons:['Complex belt path','Both motors always active','Belt tension critical','Harder to build/calibrate'] },
    { name:'H-Bot',        x:360, color:'#a04010',
      pros:['Simpler belt path','Also stationary motors','Lower cost than CoreXY','Good speed'],
      cons:['Belt racking force','Lateral frame stress','Less popular/supported','Requires stiff frame'] },
    { name:'Cartesian (bed-slinger)', x:640, color:'#7a4a28',
      pros:['Simplest mechanism','Easy to build & debug','Motor isolation clear','Cheap & well-supported'],
      cons:['Bed moves (Y inertia)','Heavier print head limits speed','Z-axis separate','Speed limited by bed mass'] },
  ];

  function MiniSchematic({x, y, type, color}) {
    if (type==='corexy') {
      return <g>
        <rect x={x} y={y} width={80} height={60} rx={3} fill="none" stroke="#7a2e00" strokeWidth="2"/>
        {/* Both motors left side */}
        <rect x={x-18} y={y+6} width={14} height={14} rx={2} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
        <rect x={x-18} y={y+40} width={14} height={14} rx={2} fill="#7a2e00" stroke="#5a1e00" strokeWidth="1.5"/>
        {/* Crossing belts */}
        <line x1={x-4} y1={y+13} x2={x+40} y2={y+30} stroke="#c45a10" strokeWidth="2.5"/>
        <line x1={x+80} y1={y+13} x2={x+40} y2={y+30} stroke="#c45a10" strokeWidth="2.5"/>
        <line x1={x-4} y1={y+47} x2={x+40} y2={y+30} stroke="#7a2e00" strokeWidth="2.5"/>
        <line x1={x+80} y1={y+47} x2={x+40} y2={y+30} stroke="#7a2e00" strokeWidth="2.5"/>
        <circle cx={x+40} cy={y+30} r={7} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
      </g>;
    }
    if (type==='hbot') {
      return <g>
        <rect x={x} y={y} width={80} height={60} rx={3} fill="none" stroke="#7a2e00" strokeWidth="2"/>
        {/* One long belt loop */}
        <rect x={x-18} y={y+23} width={14} height={14} rx={2} fill="#a04010" stroke="#7a2e00" strokeWidth="1.5"/>
        <line x1={x-4} y1={y+27} x2={x+80} y2={y+27} stroke="#a04010" strokeWidth="2.5"/>
        <line x1={x-4} y1={y+33} x2={x+80} y2={y+33} stroke="#a04010" strokeWidth="2.5"/>
        {/* H crossbar */}
        <rect x={x+30} y={y+10} width={20} height={40} rx={2} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
        <circle cx={x+40} cy={y+30} r={6} fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
      </g>;
    }
    // cartesian
    return <g>
      <rect x={x} y={y} width={80} height={60} rx={3} fill="none" stroke="#7a2e00" strokeWidth="2"/>
      {/* X motor top */}
      <rect x={x+30} y={y-14} width={18} height={14} rx={2} fill="#7a4a28" stroke="#7a2e00" strokeWidth="1.5"/>
      {/* Y motor left */}
      <rect x={x-14} y={y+23} width={14} height={14} rx={2} fill="#7a4a28" stroke="#7a2e00" strokeWidth="1.5"/>
      {/* Bed (moves in Y) */}
      <rect x={x+8} y={y+30} width={64} height={22} rx={3} fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5" opacity="0.8"/>
      {/* Head (moves in X only) */}
      <line x1={x+4} y1={y+14} x2={x+76} y2={y+14} stroke="#7a4a28" strokeWidth="2.5"/>
      <circle cx={x+40} cy={y+14} r={6} fill="#7a4a28" stroke="#5a2e00" strokeWidth="1.5"/>
    </g>;
  }

  // Performance bar chart
  function PerfBar({x, y, w, label, values, colors}) {
    const barH=14, gap=5;
    return <g>
      <text x={x} y={y-4} fontSize="10" fontWeight="700" fill="#7a2e00">{label}</text>
      {values.map((v,i)=>(
        <g key={i}>
          <rect x={x} y={y+i*(barH+gap)} width={w*v/100} height={barH} rx={2}
            fill={colors[i]} opacity="0.85"/>
          <text x={x+w+6} y={y+i*(barH+gap)+barH*0.75} fontSize="9" fill={colors[i]} fontWeight="700">
            {v}%
          </text>
        </g>
      ))}
    </g>;
  }

  const sysColors=['#c45a10','#a04010','#7a4a28'];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>CoreXY vs H-Bot vs Cartesian — Architecture Comparison and Performance Profile</div>
      <svg viewBox="0 0 1100 320" style={{width:'100%',display:'block'}}>
        {/* Schematics */}
        <MiniSchematic x={60}  y={50} type="corexy"    color="#c45a10"/>
        <MiniSchematic x={340} y={50} type="hbot"       color="#a04010"/>
        <MiniSchematic x={620} y={50} type="cartesian"  color="#7a4a28"/>

        {/* System names */}
        {systems.map((sys,i)=>(
          <text key={i} x={sys.x+40} y={38} textAnchor="middle"
            fontSize="12" fontWeight="700" fill={sys.color}>{sys.name}</text>
        ))}

        {/* Pros/Cons */}
        {systems.map((sys,i)=>(
          <g key={i}>
            {sys.pros.map((p,j)=>(
              <text key={j} x={sys.x-22} y={130+j*16} fontSize="9.5" fill="#5a7820">✓ {p}</text>
            ))}
            {sys.cons.map((c,j)=>(
              <text key={j} x={sys.x-22} y={202+j*16} fontSize="9.5" fill="#8a1010">✗ {c}</text>
            ))}
          </g>
        ))}

        {/* Performance panel — x=860 width=230 → right edge 1090 < 1100 ✓ */}
        <rect x={860} y={20} width={230} height={290} rx={5} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={975} y={38} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Performance</text>
        {[
          {label:'Max speed',           vals:[95,80,55]},
          {label:'Low inertia',         vals:[90,85,40]},
          {label:'Simplicity',          vals:[45,60,95]},
          {label:'Accuracy',            vals:[88,72,80]},
          {label:'Build ease',          vals:[40,55,92]},
        ].map((metric,mi)=>(
          <g key={mi}>
            <text x={870} y={56+mi*48} fontSize="9.5" fontWeight="700" fill="#7a2e00">{metric.label}</text>
            {metric.vals.map((v,vi)=>(
              <rect key={vi} x={870} y={60+mi*48+vi*12} width={v*1.5} height={9} rx={2}
                fill={sysColors[vi]} opacity="0.85"/>
            ))}
          </g>
        ))}
        {/* Legend — 3 entries, all fit at bottom of panel */}
        {['CoreXY','H-Bot','Cartesian'].map((l,i)=>(
          <g key={i}>
            <rect x={870} y={297+i*0} width={0} height={0}/>
          </g>
        ))}
        <line x1={870} y1={296} x2={1082} y2={296} stroke="#e8b07a" strokeWidth="0.8"/>
        {['CoreXY','H-Bot','Cartesian'].map((l,i)=>(
          <g key={i}>
            <rect x={870+i*74} y={302} width={12} height={9} rx={2} fill={sysColors[i]} opacity="0.85"/>
            <text x={886+i*74} y={310} fontSize="8.5" fill={sysColors[i]} fontWeight="700">{l}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>CoreXY wins on speed and inertia but loses on simplicity — the H-bot shares the same motor advantages but suffers from belt-racking forces; the classic Cartesian bed-slinger is easiest to build but slowest</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Mechanical components: linear rails, pulleys, belt tension
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Mechanical components: linear rails, pulleys, belt tension
// Layout: Panel1 x=0..230 | divider 230 | Panel2 x=240..530 | divider 530 | Panel3 x=540..980
// All labels stay within their own panel's x-range
// ─────────────────────────────────────────────────────────────────────────────
function DiagramMechanicalComponents() {
  const W=980, H=420;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Mechanical Components — Linear Rails · GT2 Belt · Pulleys · Tensioner · Motor Coupling</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>{arrow('arMC')}</defs>

        {/* Panel dividers */}
        <line x1={230} y1={20} x2={230} y2={H-20} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>
        <line x1={530} y1={20} x2={530} y2={H-20} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>

        {/* ── Panel 1: Linear Rail  (x: 0–230, centre at 115) ── */}
        {(()=>{
          const cx=115, cy=170;
          return <g>
            <text x={115} y={26} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Linear Rail (MGN12)</text>
            {/* Rail body */}
            <rect x={cx-52} y={cy-10} width={104} height={22} rx={4}
              fill="#e8b07a" stroke="#7a2e00" strokeWidth="2"/>
            {/* V-grooves */}
            <polygon points={`${cx-46},${cy-10} ${cx-38},${cy-2} ${cx-46},${cy+6}`} fill="#c45a10" opacity="0.7"/>
            <polygon points={`${cx+46},${cy-10} ${cx+38},${cy-2} ${cx+46},${cy+6}`} fill="#c45a10" opacity="0.7"/>
            {/* Carriage block */}
            <rect x={cx-36} y={cy-22} width={72} height={26} rx={3}
              fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
            {/* Ball bearings */}
            {[-16,-8,0,8,16].map((dx,i)=>(
              <circle key={i} cx={cx+dx} cy={cy-9} r={4} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1"/>
            ))}
            {/* Mounting holes */}
            {[-28,28].map((dx,i)=>(
              <circle key={i} cx={cx+dx} cy={cy-9} r={5}
                fill="none" stroke="#7a2e00" strokeWidth="1.5" strokeDasharray="2,1"/>
            ))}
            {/* All labels below the diagram — centred, safe within x=0..230 */}
            <line x1={cx} y1={cy+12} x2={cx} y2={cy+36} stroke="#7a2e00" strokeWidth="1.2"/>
            <text x={cx} y={cy+50} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#7a2e00">Carriage block · MGN12H</text>
            <text x={cx} y={cy+64} textAnchor="middle" fontSize="9"   fill="#a04010">Rail body: Al alloy or steel</text>
            <text x={cx} y={cy+80} textAnchor="middle" fontSize="9"   fill="#a04010">12 mm width · recirculating ball</text>
          </g>;
        })()}

        {/* ── Panel 2: GT2 Belt  (x: 240–530, belt bx=248) ── */}
        {(()=>{
          const bx=250, by=110;
          const bw=220; // belt width — fits within 250..480, well inside panel
          return <g>
            <text x={385} y={26} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">GT2 Belt Cross-Section</text>
            {/* Belt body */}
            <rect x={bx} y={by} width={bw} height={28} rx={3}
              fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
            {/* Core lines */}
            {[by+9,by+14,by+19].map((ly,i)=>(
              <line key={i} x1={bx+4} y1={ly} x2={bx+bw-4} y2={ly}
                stroke="#c45a10" strokeWidth="1.2" opacity="0.6"/>
            ))}
            {/* Teeth — 20 teeth across 220px = 11px each */}
            {Array.from({length:20},(_,ti)=>(
              <rect key={ti} x={bx+ti*11+3} y={by+26} width={7} height={10} rx={2}
                fill="#a04010" stroke="#7a2e00" strokeWidth="0.8"/>
            ))}
            {/* Pitch annotation — two arrowhead lines under first tooth gap */}
            <line x1={bx+3}  y1={by+44} x2={bx+12} y2={by+44} stroke="#c45a10" strokeWidth="2" markerEnd="url(#arMC)"/>
            <line x1={bx+14} y1={by+44} x2={bx+5}  y2={by+44} stroke="#c45a10" strokeWidth="2" markerEnd="url(#arMC)"/>
            <text x={bx+8} y={by+58} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#c45a10">2 mm pitch</text>
            {/* Width annotation on right */}
            <line x1={bx+bw+8} y1={by}    x2={bx+bw+8} y2={by+28} stroke="#7a2e00" strokeWidth="1.5"/>
            <text x={bx+bw+12} y={by+17} fontSize="9.5" fill="#7a2e00">6 mm</text>
            {/* Core label above */}
            <line x1={bx+bw/2} y1={by} x2={bx+bw/2} y2={by-18} stroke="#7a2e00" strokeWidth="1.2"/>
            <text x={bx+bw/2} y={by-24} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#7a2e00">Fibreglass core</text>
            <text x={bx+bw/2} y={by-11} textAnchor="middle" fontSize="9" fill="#a04010">low stretch · high modulus</text>
            {/* Material table — x range 250..490, inside panel */}
            {[
              ['Material','Stretch','Use'],
              ['Neoprene+Glass','Low','Standard'],
              ['Neoprene+Kevlar','Very low','High perf.'],
              ['Polyurethane+Steel','Medium','Heavy duty'],
            ].map(([a,b,c],i)=>(
              <g key={i}>
                <rect x={bx} y={by+76+i*22} width={230} height={21}
                  fill={i===0?'#a04010':i%2===0?'#fdf4ec':'#fdf0e4'}
                  stroke="#e8b07a" strokeWidth={i===0?0:0.8} rx={i===0?3:0}/>
                <text x={bx+8}   y={by+91+i*22} fontSize={i===0?9:9.5} fontWeight={i===0?'700':'400'}
                  fill={i===0?'#fdf4ec':'#1a0e06'}>{a}</text>
                <text x={bx+140} y={by+91+i*22} fontSize={i===0?9:9.5} fontWeight={i===0?'700':'400'}
                  fill={i===0?'#fdf4ec':'#a04010'}>{b}</text>
                <text x={bx+192} y={by+91+i*22} fontSize={i===0?9:9.5} fontWeight={i===0?'700':'400'}
                  fill={i===0?'#fdf4ec':'#7a2e00'}>{c}</text>
              </g>
            ))}
          </g>;
        })()}

        {/* ── Panel 3: Tensioner  (x: 540–980) — motor at x=580, belt ends at 580+56+14+260=910 < 980 ── */}
        {(()=>{
          const tx=590, ty=76; // motor top-left x, top y offset
          const motorW=56, motorH=56;
          const shaftW=14, pulleyR=13;
          const beltStart = tx+motorW+shaftW+pulleyR*2; // x=590+56+14+26=686
          const beltEnd   = 955; // well within 980

          return <g>
            <text x={762} y={26} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Belt Tensioner &amp; Motor Mount</text>

            {/* Slotted mount slots — LEFT of motor */}
            {[ty+14, ty+motorH-8].map((sy,i)=>(
              <rect key={i} x={tx-22} y={sy} width={22} height={8} rx={2}
                fill="none" stroke="#7a2e00" strokeWidth="1.5" strokeDasharray="3,2"/>
            ))}

            {/* Tension arrow pointing left — drawn ABOVE the slotted labels */}
            <line x1={tx-2} y1={ty+16} x2={tx-54} y2={ty+16}
              stroke="#c45a10" strokeWidth="2.5" markerEnd="url(#arMC)"/>
            <text x={tx-58} y={ty+20} textAnchor="end" fontSize="9.5" fontWeight="700" fill="#c45a10">Tension →</text>

            {/* Slotted mount label — below the arrow, clearly separated */}
            <text x={tx-24} y={ty+motorH+6}  textAnchor="end" fontSize="9.5" fontWeight="700" fill="#7a2e00">Slotted mount</text>
            <text x={tx-24} y={ty+motorH+19} textAnchor="end" fontSize="9"   fill="#a04010">for tensioning</text>

            {/* Motor block */}
            <rect x={tx} y={ty} width={motorW} height={motorH} rx={4}
              fill="#c45a10" stroke="#7a2e00" strokeWidth="2.5"/>
            <text x={tx+motorW/2} y={ty+motorH/2+4} textAnchor="middle" fontSize="10" fontWeight="700" fill="#fdf4ec">MOTOR</text>

            {/* Shaft */}
            <rect x={tx+motorW} y={ty+motorH/2-7} width={shaftW} height={14} rx={2}
              fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>

            {/* Pulley */}
            <circle cx={tx+motorW+shaftW+pulleyR} cy={ty+motorH/2} r={pulleyR}
              fill="#e8b07a" stroke="#7a2e00" strokeWidth="2"/>
            <circle cx={tx+motorW+shaftW+pulleyR} cy={ty+motorH/2} r={5}
              fill="#fdf4ec" stroke="#7a2e00" strokeWidth="1.5"/>

            {/* Belt strands — capped at 940 to stay visibly within 980 viewBox */}
            <line x1={beltStart} y1={ty+motorH/2-6} x2={940} y2={ty+motorH/2-6}
              stroke="#a04010" strokeWidth="5" strokeLinecap="round"/>
            <line x1={beltStart} y1={ty+motorH/2+6} x2={940} y2={ty+motorH/2+6}
              stroke="#a04010" strokeWidth="5" strokeLinecap="round"/>
            {/* Teeth on bottom belt strand — 20 teeth fits within beltStart..940 */}
            {Array.from({length:20},(_,ti)=>(
              <rect key={ti} x={beltStart+ti*12} y={ty+motorH/2+6} width={8} height={6} rx={1}
                fill="#7a2e00" opacity="0.7"/>
            ))}

            {/* Tension specs — start well below slotted label (which ends at ty+motorH+19) */}
            {[
              ['Target tension:',   '40–60 N per belt segment'],
              ['Test method:',      'Belt frequency with phone app'],
              ['Typical frequency:','~100–140 Hz (GT2, 300 mm span)'],
              ['Under-tension:',    'Skipping, ringing artefacts'],
              ['Over-tension:',     'Bearing wear, frame flex'],
              ['Re-tension:',       'After first 20 h then as needed'],
            ].map(([l,v],i)=>(
              <g key={i}>
                <text x={tx} y={ty+motorH+42+i*26} fontSize="9.5" fontWeight="700" fill="#7a2e00">{l}</text>
                <text x={tx} y={ty+motorH+56+i*26} fontSize="9.5" fill="#a04010" fontFamily="'Courier New',monospace">{v}</text>
              </g>
            ))}
          </g>;
        })()}
      </svg>
      <div style={S.dCaption}>MGN12 or MGN9 rails on the X-axis carriage and Y-axis gantry give the smoothest motion; GT2 belt with fibreglass or Kevlar core minimises stretch; correct belt tension (measured by resonant frequency) is critical for print quality</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Speed and acceleration capability (bar chart) vs architecture
// ─────────────────────────────────────────────────────────────────────────────
function DiagramSpeedAccel() {
  const W=980, H=340;

  const configs = [
    { label:'Cartesian\nbed-slinger',    speed:120, accel:1500,  note:'Bed mass limits Y', color:'#7a4a28' },
    { label:'H-Bot',                     speed:300, accel:4000,  note:'Belt racking limits',color:'#a04010' },
    { label:'CoreXY\n(standard)',        speed:400, accel:8000,  note:'GT2 6mm, 1.8°motor',color:'#c45a10' },
    { label:'CoreXY\n(tuned)',           speed:600, accel:15000, note:'Input shaping, 0.9°',color:'#d4874a' },
    { label:'CoreXY\n(high-performance)',speed:900, accel:30000, note:'Klipper IS, CAN bus', color:'#e8b07a' },
    { label:'CoreXY\n(bleeding edge)',   speed:1500,accel:60000, note:'Voron, 48V, tuned',  color:'#c4a010' },
  ];

  const maxSpeed=1600, maxAccel=65000;
  const baseY=220, maxH=170;
  const barW=90, gap=22, startX=50;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Achievable Speed &amp; Acceleration — CoreXY Configurations vs. Other Architectures</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>{arrow('arSA')}{arrowBack('arSAb')}</defs>

        {/* Y axes */}
        <line x1={40} y1={baseY-maxH-10} x2={40} y2={baseY+4} stroke="#7a2e00" strokeWidth="1.8"/>
        <line x1={40} y1={baseY} x2={W-20} y2={baseY} stroke="#7a2e00" strokeWidth="1.8"/>
        {/* Speed Y axis */}
        {[0,300,600,900,1200,1500].map(v=>{
          const y=baseY-(v/maxSpeed)*maxH;
          return <g key={v}>
            <line x1={36} y1={y} x2={W-20} y2={y} stroke="#e8b07a" strokeWidth="0.8" strokeDasharray="4,3"/>
            <text x={34} y={y+4} textAnchor="end" fontSize="9" fill="#7a4a28">{v}</text>
          </g>;
        })}
        <text x={14} y={baseY-maxH/2} textAnchor="middle" fontSize="10" fontWeight="700" fill="#c45a10"
          transform={`rotate(-90,14,${baseY-maxH/2})`}>Speed (mm/s)</text>

        {/* Bars */}
        {configs.map((cfg,i)=>{
          const bx = startX + i*(barW+gap);
          const speedH = (cfg.speed/maxSpeed)*maxH;
          const accelNorm = Math.min(cfg.accel, maxAccel);
          const accelH = (accelNorm/maxAccel)*maxH;

          return (
            <g key={i}>
              {/* Speed bar (left half) */}
              <rect x={bx} y={baseY-speedH} width={barW*0.48} height={speedH} rx="3 0 0 0"
                fill={cfg.color} stroke="#7a2e00" strokeWidth="1.2" opacity="0.9"/>
              {/* Accel bar (right half) — normalised to same axis height for comparison */}
              <rect x={bx+barW*0.52} y={baseY-accelH} width={barW*0.48} height={accelH} rx="0 3 0 0"
                fill={cfg.color} stroke="#7a2e00" strokeWidth="1.2" opacity="0.6"
                strokeDasharray="3,2"/>

              {/* Values above bars */}
              <text x={bx+barW*0.24} y={baseY-speedH-6} textAnchor="middle"
                fontSize="9" fontWeight="700" fill={cfg.color}>{cfg.speed}</text>
              <text x={bx+barW*0.76} y={baseY-accelH-6} textAnchor="middle"
                fontSize="8.5" fill="#7a4a28">{cfg.accel>=1000?(cfg.accel/1000).toFixed(0)+'k':cfg.accel}</text>

              {/* Labels below */}
              {cfg.label.split('\n').map((l,j)=>(
                <text key={j} x={bx+barW/2} y={baseY+16+j*14} textAnchor="middle"
                  fontSize="9" fontWeight="700" fill="#7a2e00">{l}</text>
              ))}
              <text x={bx+barW/2} y={baseY+50} textAnchor="middle"
                fontSize="8" fill="#a04010" fontStyle="italic">{cfg.note}</text>
            </g>
          );
        })}

        {/* Legend */}
        <rect x={W-200} y={14} width={10} height={10} rx={2}
          fill="#c45a10" opacity="0.9"/>
        <text x={W-185} y={24} fontSize="10" fontWeight="700" fill="#c45a10">Solid = Speed (mm/s)</text>
        <rect x={W-200} y={32} width={10} height={10} rx={2} fill="#c45a10" opacity="0.6"
          stroke="#7a2e00" strokeWidth="1" strokeDasharray="3,2"/>
        <text x={W-185} y={43} fontSize="10" fontWeight="700" fill="#7a4a28">Dashed = Accel (mm/s²)</text>
        <text x={W-185} y={57} fontSize="9" fill="#a04010">(accel normalised to same scale)</text>

        {/* Accel Y axis label */}
        <text x={W-12} y={baseY-maxH/2} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a4a28"
          transform={`rotate(90,${W-12},${baseY-maxH/2})`}>Accel (mm/s²) — normalised</text>
      </svg>
      <div style={S.dCaption}>CoreXY's low-inertia architecture enables 10–20× higher acceleration than bed-slinger designs; the primary limiters are belt stretch, stepper motor torque curve, and resonance — addressed by Klipper input shaping</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 7 — Input shaping: resonance and compensation
// ─────────────────────────────────────────────────────────────────────────────
function DiagramInputShaping() {
  const W=980, H=320;

  // Simulated frequency response curves
  function sinc(x) { return x===0 ? 1 : Math.sin(Math.PI*x)/(Math.PI*x); }
  function gaussian(x, mu, sigma) { return Math.exp(-0.5*((x-mu)/sigma)**2); }

  const freqs = Array.from({length:200}, (_,i) => i*0.5); // 0–100 Hz
  const baseY = 200, scaleY = 120;

  // Uncompensated response (sharp resonance peak ~45 Hz)
  const uncompensated = freqs.map(f =>
    baseY - scaleY*(gaussian(f,45,4)*1.2 + 0.15 + gaussian(f,20,6)*0.3)
  );

  // EI (Extra-Insane / ZV) shaped response — suppressed peak
  const shaped = freqs.map(f =>
    baseY - scaleY*(gaussian(f,45,4)*0.18 + 0.12 + gaussian(f,20,6)*0.08)
  );

  const pathFromData = (ys) => {
    const pts = freqs.map((f,i) => `${60+i*4.4},${ys[i]}`);
    return 'M ' + pts.join(' L ');
  };

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Resonance &amp; Input Shaping — Klipper ADXL345 Calibration, ZV/EI Filters, Ringing Suppression</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>{arrow('arIS')}</defs>

        {/* Chart background */}
        <rect x={50} y={20} width={560} height={260} rx={5} fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1.5"/>

        {/* Grid lines */}
        {[0,25,50,75,100].map(f=>{
          const x=60+f*4.4*2;
          return <g key={f}>
            <line x1={x} y1={24} x2={x} y2={276} stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>
            <text x={x} y={290} textAnchor="middle" fontSize="9.5" fill="#7a4a28">{f}</text>
          </g>;
        })}
        {[0,0.25,0.5,0.75,1.0].map((v,i)=>{
          const y=baseY-v*scaleY;
          return <g key={i}>
            <line x1={50} y1={y} x2={610} y2={y} stroke="#e8b07a" strokeWidth="0.8" strokeDasharray="4,3"/>
          </g>;
        })}

        {/* X/Y axis labels */}
        <text x={330} y={308} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Frequency (Hz)</text>
        <text x={22} y={baseY} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00"
          transform={`rotate(-90,22,${baseY})`}>Response Amplitude</text>

        {/* Uncompensated curve */}
        <path d={pathFromData(uncompensated)} fill="none" stroke="#8a1010" strokeWidth="2.5"/>
        {/* Shaped curve */}
        <path d={pathFromData(shaped)} fill="none" stroke="#5a7820" strokeWidth="2.5"/>

        {/* Resonance peak annotation */}
        <line x1={258} y1={baseY-scaleY*1.35} x2={258} y2={baseY-scaleY*1.1}
          stroke="#8a1010" strokeWidth="1.5" strokeDasharray="3,2"/>
        <rect x={210} y={20} width={118} height={34} rx={4} fill="#fdf0e4" stroke="#8a1010" strokeWidth="1.5"/>
        <text x={269} y={36} textAnchor="middle" fontSize="10" fontWeight="700" fill="#8a1010">Resonance peak</text>
        <text x={269} y={50} textAnchor="middle" fontSize="9.5" fill="#8a1010">~45 Hz (typical CoreXY)</text>

        {/* Shaped result annotation */}
        <rect x={52} y={164} width={130} height={34} rx={4} fill="#fdf0e4" stroke="#5a7820" strokeWidth="1.5"/>
        <text x={117} y={180} textAnchor="middle" fontSize="10" fontWeight="700" fill="#5a7820">After shaping</text>
        <text x={117} y={194} textAnchor="middle" fontSize="9.5" fill="#5a7820">85–95% reduction</text>

        {/* Legend */}
        <line x1={56} y1={110} x2={88} y2={110} stroke="#8a1010" strokeWidth="2.5"/>
        <text x={94} y={114} fontSize="10" fontWeight="700" fill="#8a1010">Uncompensated (ringing)</text>
        <line x1={56} y1={128} x2={88} y2={128} stroke="#5a7820" strokeWidth="2.5"/>
        <text x={94} y={132} fontSize="10" fontWeight="700" fill="#5a7820">Input shaped (ZV/EI filter)</text>

        {/* Right panel — process steps */}
        <rect x={628} y={14} width={342} height={290} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={799} y={34} textAnchor="middle" fontSize="12" fontWeight="700" fill="#7a2e00">Input Shaping Process</text>
        <line x1={636} y1={42} x2={962} y2={42} stroke="#e8b07a"/>
        {[
          ['1. Mount ADXL345:','Accelerometer on toolhead'],
          ['',                 'Connected via SPI to Raspberry Pi'],
          ['2. Run test:',     'Klipper TEST_RESONANCES command'],
          ['',                 'Sweeps 5–133 Hz, records acceleration'],
          ['3. Analyse:',      'generate_resonances.py → CSV + PNG'],
          ['',                 'Identify peak frequency and Q factor'],
          ['4. Select shaper:','ZV: simple, low latency, good for low Q'],
          ['',                 'MZV: moderate performance, balanced'],
          ['',                 'EI: aggressive, higher accel possible'],
          ['',                 'ZVDD: very high Q systems'],
          ['5. Configure:',    '[input_shaper] in printer.cfg'],
          ['',                 'shaper_freq_x: 45.0'],
          ['',                 'shaper_type_x: mzv'],
          ['6. Validate:',     'Print ringing test object at speed'],
          ['',                 'Compare before/after photos'],
          ['Benefit:',         'Often 3–10× higher max speed'],
          ['',                 'for same quality output'],
        ].map(([l,v],i)=>(
          <g key={i}>
            <text x={636} y={56+i*14} fontSize="10" fontWeight={l?'700':'400'} fill="#7a2e00">{l}</text>
            <text x={l?740:642} y={56+i*14} fontSize="10" fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Input shaping (pressure advance compensation for resonance) is a firmware-level filter that pre-distorts the motion profile to cancel structural vibration — Klipper implements ZV, MZV, EI, ZVDD, and 2HUMP_EI shapers</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 8 — Popular CoreXY implementations: Voron, Prusa, RatRig, HEVORT
// ─────────────────────────────────────────────────────────────────────────────
function DiagramImplementations() {
  const W=980, H=300;

  const machines = [
    { name:'Voron 2.4',       x:60,  color:'#c45a10',
      specs:['Frame: 2020/2040 Al extrusion','Motion: 4 leadscrews (Z), CoreXY (XY)','Extruder: CW2 direct drive','Print vol: 250³–350³ mm','Top speed: ~500 mm/s typical','Notable: flying gantry, 4-point z-tram'] },
    { name:'Voron Trident',   x:300, color:'#a04010',
      specs:['Frame: 2020/2040 Al extrusion','Motion: 3 leadscrews (Z), CoreXY','Extruder: Orbiter, Galileo2','Print vol: 250³ mm','Top speed: ~400 mm/s typical','Notable: simpler Z than V2.4'] },
    { name:'RatRig V-Core 4', x:540, color:'#7a4a28',
      specs:['Frame: heavy-gauge Al extrusion','Motion: 3-point Z, CoreXY (XY)','Extruder: EVA toolhead system','Print vol: 300³–500³ mm','Top speed: ~600 mm/s capable','Notable: modular toolhead, open-source'] },
    { name:'Bambu Lab X1',    x:780, color:'#8a6020',
      specs:['Frame: magnesium alloy die-cast','Motion: CoreXY, integrated sensors','Extruder: multi-colour AMS','Print vol: 256×256×256 mm','Top speed: 500 mm/s, 20k mm/s²','Notable: closed-source, AI monitoring'] },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Popular CoreXY Implementations — Voron · RatRig · Bambu Lab · Key Design Choices</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {machines.map((m,i)=>{
          const cw=200;
          return (
            <g key={i}>
              {/* Card */}
              <rect x={m.x} y={14} width={cw} height={H-28} rx={6}
                fill={i%2===0?'#fdf4ec':'#fdf0e4'} stroke={m.color} strokeWidth="2"/>
              {/* Header */}
              <rect x={m.x} y={14} width={cw} height={32} rx={6} fill={m.color} stroke="none"/>
              <rect x={m.x} y={38} width={cw} height={8} fill={m.color} stroke="none"/>
              <text x={m.x+cw/2} y={34} textAnchor="middle" fontSize="11.5" fontWeight="700" fill="#fdf4ec">
                {m.name}
              </text>
              {/* Specs */}
              {m.specs.map((spec,j)=>{
                const [label, ...rest] = spec.split(': ');
                return (
                  <g key={j}>
                    <text x={m.x+10} y={64+j*34} fontSize="9.5" fontWeight="700" fill="#7a2e00">{label}:</text>
                    <text x={m.x+10} y={78+j*34} fontSize="9.5" fill="#a04010" fontFamily="'Courier New',monospace">
                      {rest.join(': ')}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>CoreXY dominates high-performance desktop fabrication — the Voron ecosystem (open-source, community-built) and Bambu Lab (closed-source, commercial) represent the two poles of the CoreXY philosophy</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 9 — Stepper motor sizing and torque/speed curve
// ─────────────────────────────────────────────────────────────────────────────
function DiagramMotorSizing() {
  const W=980, H=340;

  // Torque vs speed curve for NEMA17 at different currents
  const speeds = Array.from({length:50},(_,i)=>i*60); // 0–2940 rpm
  const maxRPM=2940;

  function torque(rpm, holdingNm, stallRPM) {
    // Simplified linear torque rolloff
    return Math.max(0, holdingNm * (1 - rpm/stallRPM));
  }

  const motors = [
    { label:'NEMA17 0.4A (weak)', holdNm:0.25, stallRPM:2400, color:'#d4874a' },
    { label:'NEMA17 1.0A (std)',  holdNm:0.42, stallRPM:2000, color:'#c45a10' },
    { label:'NEMA17 1.5A (high)', holdNm:0.55, stallRPM:1600, color:'#7a2e00' },
    { label:'NEMA17 48V drive',   holdNm:0.55, stallRPM:3200, color:'#5a7820' },
  ];

  const chartX=60, chartY=20, chartW=520, chartH=240;
  const maxTorque=0.65;

  function toChart(rpm, Nm) {
    return [
      chartX + (rpm/maxRPM)*chartW,
      chartY + chartH - (Nm/maxTorque)*chartH,
    ];
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Stepper Motor Sizing — NEMA17 Torque/Speed Curves · Current Drivers · Steps &amp; Microstepping</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>{arrow('arMS')}</defs>

        {/* Chart background */}
        <rect x={chartX} y={chartY} width={chartW} height={chartH}
          fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1.5" rx={4}/>

        {/* Grid */}
        {[0,0.1,0.2,0.3,0.4,0.5,0.6].map(v=>{
          const y=chartY+chartH-(v/maxTorque)*chartH;
          return <g key={v}>
            <line x1={chartX} y1={y} x2={chartX+chartW} y2={y}
              stroke="#e8b07a" strokeWidth="0.8" strokeDasharray="4,3"/>
            <text x={chartX-6} y={y+4} textAnchor="end" fontSize="9.5" fill="#7a4a28">{v.toFixed(1)}</text>
          </g>;
        })}
        {[0,600,1200,1800,2400,2940].map(v=>{
          const x=chartX+(v/maxRPM)*chartW;
          return <g key={v}>
            <line x1={x} y1={chartY} x2={x} y2={chartY+chartH}
              stroke="#e8b07a" strokeWidth="0.8" strokeDasharray="4,3"/>
            <text x={x} y={chartY+chartH+16} textAnchor="middle" fontSize="9.5" fill="#7a4a28">{v}</text>
          </g>;
        })}

        {/* Axis labels */}
        <text x={chartX+chartW/2} y={chartY+chartH+28} textAnchor="middle"
          fontSize="11" fontWeight="700" fill="#7a2e00">Motor Speed (RPM)</text>
        <text x={chartX-42} y={chartY+chartH/2} textAnchor="middle"
          fontSize="11" fontWeight="700" fill="#7a2e00"
          transform={`rotate(-90,${chartX-42},${chartY+chartH/2})`}>Torque (N·m)</text>

        {/* Torque curves */}
        {motors.map((m,mi)=>{
          const pts = speeds.map(rpm=>{
            const Nm=torque(rpm,m.holdNm,m.stallRPM);
            const [x,y]=toChart(rpm,Nm);
            return `${x},${y}`;
          });
          return <path key={mi} d={'M '+pts.join(' L ')}
            fill="none" stroke={m.color} strokeWidth="2.5" opacity="0.9"/>;
        })}

        {/* Operating zone shading */}
        {(()=>{
          const zx1 = chartX+(300/maxRPM)*chartW;
          const zx2 = chartX+(900/maxRPM)*chartW;
          return <>
            <rect x={zx1} y={chartY} width={zx2-zx1} height={chartH}
              fill="#c45a10" opacity="0.07"/>
            {/* Zone label as compact box at bottom of shaded column — curves are thin here */}
            <rect x={zx1+2} y={chartY+chartH-34} width={zx2-zx1-4} height={30} rx={3}
              fill="#fdf0e4" opacity="0.92"/>
            <text x={(zx1+zx2)/2} y={chartY+chartH-20} textAnchor="middle"
              fontSize="8" fill="#c45a10" fontWeight="700">Typical</text>
            <text x={(zx1+zx2)/2} y={chartY+chartH-8} textAnchor="middle"
              fontSize="8" fill="#c45a10">CoreXY zone</text>
          </>;
        })()}

        {/* Motor legend — ONE row below x-axis RPM label. chartY+chartH+32=292 is label, legend at ~310 */}
        <rect x={chartX} y={chartY+chartH+40} width={chartW} height={22} rx={3}
          fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1"/>
        {motors.map((m,i)=>(
          <g key={i}>
            <line x1={chartX+8+i*130}    y1={chartY+chartH+51}
                  x2={chartX+28+i*130}   y2={chartY+chartH+51}
              stroke={m.color} strokeWidth="2.8"/>
            <text x={chartX+32+i*130} y={chartY+chartH+55}
              fontSize="9" fill={m.color} fontWeight="700">{m.label}</text>
          </g>
        ))}

        {/* Right info panel */}
        <rect x={608} y={14} width={360} height={280} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={788} y={34} textAnchor="middle" fontSize="12" fontWeight="700" fill="#7a2e00">Motor &amp; Driver Specs</text>
        <line x1={616} y1={42} x2={960} y2={42} stroke="#e8b07a"/>
        {[
          ['Standard motor:',  'NEMA17 (42×42 mm flange)'],
          ['Common frame:',    '42×42×40 mm (1.7–2.0 A rated)'],
          ['Holding torque:',  '0.40–0.59 N·m typical'],
          ['Step angle:',      '1.8° (200 steps/rev standard)'],
          ['',                 '0.9° (400 steps/rev high-res)'],
          ['Microstepping:',   '16× standard · 32×/64× for quiet'],
          ['',                 'TMC2209/5160 (stall detection)'],
          ['Driver voltage:',  '24V (standard) · 48V (high-perf)'],
          ['',                 'Higher voltage → faster ramp-up'],
          ['Current setting:', 'Run: 70–90% rated · Hold: 50%'],
          ['Practical steps:', '80 steps/mm (GT2, 20T pulley)'],
          ['',                 'steps = 200 × microstep / (2 × π × r)'],
          ['',                 '= 200×16 / (2×2×20) = 80 steps/mm'],
          ['Z-axis:',          'Usually leadscrew (separate motor)'],
          ['',                 'T8 8mm lead → 400 steps/mm @16×'],
        ].map(([l,v],i)=>(
          <g key={i}>
            <text x={618} y={56+i*15} fontSize="10" fontWeight={l?'700':'400'} fill="#7a2e00">{l}</text>
            <text x={l?745:624} y={56+i*15} fontSize="10" fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>At 48V the same motor reaches peak torque at much higher RPM — critical for CoreXY where both motors must accelerate simultaneously; TMC2209 drivers add stall detection (sensorless homing) and quiet StealthChop mode</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROOT COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function CoreXYPage() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>CoreXY Motion System — Mechanical Engineering Reference</h1>

      <p style={S.p}>
        <strong>CoreXY</strong> is a 2D Cartesian motion architecture in which two stepper motors —
        both mounted stationary on the machine frame — drive a single toolhead carriage through a crossed
        belt arrangement. Unlike a traditional bed-slinger (where the Y-axis moves the print bed) or an
        H-bot (where one motor drives only X and the other only Y), in CoreXY <em>both motors contribute
        to every X and Y movement</em>. The result is dramatically lower moving mass, enabling acceleration
        rates 5–20× higher than conventional Cartesian machines and top speeds exceeding 1,000 mm/s in
        tuned implementations.
      </p>

      <h2 style={S.h2}>Full Anatomy &amp; System Overview</h2>
      <p style={S.p}>
        A CoreXY system consists of five main subsystems: the fixed aluminium extrusion or steel frame,
        two stepper motors fixed to that frame, a crossed belt drive system routed through corner pulleys,
        linear rails carrying the X-axis carriage, and the toolhead (print nozzle, laser, or probe) mounted
        to that carriage.
      </p>
      <DiagramAnatomy />
      <Table2 rows={[
        ['Frame material','2020/2040 aluminium extrusion: standard, lightweight, easy to assemble with T-nuts\nSteel tube / welded frame: stiffer, heavier — used in large-format or industrial CoreXY\nCast/machined aluminium: highest rigidity, used in commercial machines (Bambu Lab, Prusa XL)\nFrame rigidity directly affects resonance frequency and print quality at high speed'],
        ['Motor mounting','Both motors fixed to frame (or frame-mounted plate) — never move during operation\nTypically on the rear-left and rear-right corners, or both left-side (offset CoreXY variant)\nMotor isolation: rubber standoffs reduce high-frequency vibration transmission to frame\nAccessible for wiring; cooling fan optional at sustained high currents (≥1.2 A)'],
        ['Carriage/toolhead','Must be as light as possible: every gram adds to the inertia the motors must accelerate\nDirect drive extruder adds ~200–300 g but eliminates Bowden slack\nBowden extruder (remote) reduces carriage to ~50–100 g; enables higher acceleration\nCarriage must accommodate X-rail carriage blocks and belt clip attachment points'],
        ['X-axis linear guide','Carries the toolhead along X — bridges between the two Y-axis rails/extrusions\nMGN9 (9mm): lightweight, used on sub-250mm travel machines\nMGN12 (12mm): standard for most CoreXY; good stiffness-to-weight\nDual-rail X-axis: prevents carriage rotation under asymmetric tool loads'],
        ['Y-axis linear guides','Two Y-direction rails (or rod + bearing combos) guide the X-axis gantry fore/aft\nMust be parallel to within 0.1 mm over full length for smooth motion\nOften 2020 extrusion acts as the Y-rail surface with linear bearings (cheaper builds)\nMGN12 on both Y-axes: premium accuracy, required for sub-0.1 mm repeatability'],
      ]}/>

      <h2 style={S.h2}>Kinematics — Motor Direction to Carriage Motion</h2>
      <p style={S.p}>
        The CoreXY kinematic equations are derived from the belt geometry: Belt A connects Motor A to
        the carriage via the top-left and top-right pulleys; Belt B connects Motor B via the bottom-left
        and bottom-right pulleys. The carriage position is the algebraic combination of both belt
        displacements.
      </p>
      <DiagramKinematics />
      <Table2 rows={[
        ['Forward kinematics','If Motor A moves +N steps and Motor B moves +M steps:\n  X = (N + M) / 2\n  Y = (N − M) / 2\nFor pure +X: A=+1, B=+1 → X=+1, Y=0\nFor pure +Y: A=+1, B=−1 → X=0, Y=+1'],
        ['Inverse kinematics','Given desired carriage movement (ΔX, ΔY), compute motor steps:\n  A_steps = ΔX + ΔY\n  B_steps = ΔX − ΔY\nDiagonal +X+Y: A=2, B=0 → only Motor A moves\nDiagonal +X−Y: A=0, B=2 → only Motor B moves'],
        ['Velocity consequence','During diagonal motion, one motor carries full combined velocity\nPeak motor speed = √2 × axis peak speed for 45° diagonals\nFirmware must enforce a combined speed limit to avoid motor stall on diagonals\nKlipper/Marlin handle this automatically via vector speed limiting'],
        ['Squareness calibration','If A≠B steps/mm: the printed square will be a parallelogram (skew error)\nCorrected by rotating the entire coordinate system by the skew angle\nKlipper: [skew_correction] with SET_SKEW command\nPhysical check: print 100mm square, measure both diagonals; difference = 2× skew'],
        ['Steps per mm','Default NEMA17 (1.8°, 16× microstepping) + GT2 belt + 20T pulley:\nsteps/mm = (200 × 16) / (20 × 2) = 80 steps/mm\nWith 0.9° motor: 160 steps/mm\nWith 20T vs 16T pulley: 80 vs 100 steps/mm (smaller pulley = higher resolution, lower speed)'],
      ]}/>

      <h2 style={S.h2}>Belt Path Geometry &amp; Pulley Layout</h2>
      <p style={S.p}>
        The defining feature of CoreXY — and its main complexity — is the crossed dual-belt path.
        The two belts must physically cross inside the frame but must not touch each other. This is
        achieved by routing one belt at a higher Z-height than the other using offset idler pulleys.
      </p>
      <DiagramBeltPath />
      <Table2 rows={[
        ['Belt crossing','The two belts cross in the interior of the frame but run at different heights\nTypically 2–4 mm vertical separation at the crossing point\nAchieved by mounting idler pulleys on offset Z-height standoffs or printed spacers\nFailure to separate heights: belts rub, wear rapidly, cause motion artefacts'],
        ['GT2 belt specification','Pitch: 2 mm tooth-to-tooth\nWidth: 6 mm standard, 9 mm for higher load capacity\nMaterial: neoprene + fibreglass core (standard), neoprene + Kevlar (low stretch)\nLength: measured by total path length; closed loop with steel pin splice or open-loop with clamps'],
        ['Pulley tooth count','20T (teeth): standard — gives 40 mm/rev belt travel at 20T\n16T: higher resolution (2.5 mm/rev less travel) but lower top speed\n20T idlers: tooth-on-tooth engagement (toothed idler) or smooth-bore idler\nSmooth idler on belt back-face: less wear, simpler; toothed if belt wraps >45°'],
        ['Idler bearings','F695 (5×13×4 mm flanged): very common, cheap, sufficient for loads <50 N\nF623 (3×10×4 mm): used where space is very limited (toolhead-side idlers)\nBearing pre-load: finger-tight only — over-tightening causes binding; too loose causes wobble\nStainless bearings: preferred for dusty or humid environments'],
        ['Belt clamp at carriage','Open-loop belt ends clamp to the carriage with printed plastic or aluminium clamps\nClamp bolt tension: typically 0.5–1 N·m — enough to grip without cutting the belt\nSelf-locking design preferred: clamping harder as belt tension increases\nAfter first 20 h of use: re-check and retighten belt clamps (belt bedding-in stretch)'],
        ['Routing symmetry','Both belt paths must be symmetric about the carriage: equal total length each side\nAsymmetric paths cause differential stretch with temperature changes → position error\nBelt path length error >5 mm: may cause Y-axis squareness deviation\nSymmetry check: equal amounts of belt exposed on both sides of carriage clamp'],
      ]}/>

      <h2 style={S.h2}>CoreXY vs H-Bot vs Cartesian</h2>
      <DiagramComparison />
      <Table2 rows={[
        ['CoreXY vs H-bot','H-bot also uses two stationary motors and a single belt — but uses ONE continuous belt loop\nH-bot belt racking: lateral forces from belt asymmetry cause the X-axis to skew (rack)\nCoreXY uses two separate belts — the geometry cancels the racking force by design\nResult: CoreXY allows lighter X-axis construction; H-bot requires heavier, stiffer gantry'],
        ['CoreXY vs bed-slinger','Bed-slinger: Y-axis motor moves the entire build plate → inertia limited by bed + part mass\nCorXY: Y-axis moves only the lightweight gantry → inertia limited by gantry mass only\nFor large parts: bed-slinger inertia scales with part volume; CoreXY stays constant\nCoreXY preferred for large formats (>300 mm) due to mass advantage'],
        ['Delta kinematics','Delta (Rostock-style): three towers, three carriages, complex kinematics\nDelta advantage: very low inertia possible; fast; symmetric\nDelta disadvantage: complex calibration; non-constant accuracy across build volume\nCoreXY replaces delta in most new designs due to simpler calibration and rectangular volume'],
        ['IDEX / dual CoreXY','IDEX: Independent Dual EXtruder — two toolheads on the same X-axis\nRequires modified kinematics: one motor drives each head independently in X\nY remains CoreXY-style (both heads share Y movement)\nUsed for dual-material, mirror printing, or simultaneous production (two identical parts)'],
      ]}/>

      <h2 style={S.h2}>Mechanical Components In Depth</h2>
      <DiagramMechanicalComponents />
      <Table2 rows={[
        ['Linear rail selection','MGN9: 9mm width, light weight, suited to toolheads under 300g; suitable up to ~300mm travel\nMGN12: 12mm, standard choice — good stiffness for 300–400mm travel\nMGN15: 15mm, heavy builds or where lateral stiffness is critical\nRail material: hardened steel (standard), stainless (humid/corrosive environments)\nRail tolerance: H (standard), C (higher preload, less play — preferred for CoreXY X-axis)'],
        ['Carriage block types','MGN9C / MGN12C: short block — lighter, less preload, slight roll play\nMGN9H / MGN12H: long block — more preload, better rigidity under moment loads\nFor toolhead: long block preferred on X-axis; short blocks acceptable on Y-axis (moment-free)\nPreload classes: C0 (clearance), C1 (zero), C2 (light), C3 (medium) — C1/C2 for CoreXY'],
        ['Belt tensioning methods','Motor-slide: motor on slotted plate, slide motor back to increase belt tension\nSpring tensioner: spring-loaded idler takes up slack automatically — self-adjusting\nAdjustment screw: printed or machined arm with adjustment bolt — precise, repeatable\nTarget tension: 40–60 N per belt strand; measure with belt frequency app (~100–140 Hz for 300 mm span)'],
        ['Frame squareness','Critical for print accuracy — frame must be square to <0.5 mm over its longest diagonal\nSetup method: use a large square or measure corner-to-corner diagonals (must be equal)\nBrackets vs threaded rods: 3-point contact brackets maintain squareness better than rod tension alone\nRe-check squareness after: any shipping/relocation, bearing replacement, or crash event'],
      ]}/>

      <h2 style={S.h2}>Speed and Acceleration Capability</h2>
      <DiagramSpeedAccel />
      <Table2 rows={[
        ['Speed limiting factors','Belt stretch: GT2 fibreglass stretches ~0.1% under tension → position error at high acceleration\nMotor torque rolloff: NEMA17 loses torque above ~800 RPM at 24V; use 48V to extend\nResonance: frame/belt resonance causes ringing artefacts at resonant speed — use input shaping\nFirmware: older 8-bit firmware (AVR) limits planning speed; 32-bit (STM32, RP2040) removes this limit'],
        ['Acceleration limits','Higher acceleration = shorter distance to reach target speed = faster print time\nJerk / junction deviation: controls speed change at direction changes (sharp corners)\nKlipper default max_accel: 3000 mm/s² — conservative starting point\nWith input shaping enabled: 10,000–30,000 mm/s² achievable on well-tuned CoreXY'],
        ['Klipper advantages','Klipper runs motion planning on Raspberry Pi (ARM) — far more computational power\nReal-time kinematics allows smooth velocity curves (not trapezoidal like Marlin)\nInput shaping: ADXL345 accelerometer calibration baked into firmware\nPressure advance: pre-charges extruder for sharp corners — analogous to input shaping for extrusion'],
        ['Print quality vs speed','Quality is not directly proportional to speed — it depends on speed relative to resonant frequencies\nBest quality: print below the first resonance peak, or at speed where input shaping is calibrated\nLayer time: at 300 mm/s, a 100×100 mm perimeter takes ~1.3 s — meaningful productivity gain\nOverhangs and bridges: geometry limits, not motion limits — typically max 30–45° without support'],
      ]}/>

      <h2 style={S.h2}>Input Shaping &amp; Resonance Control</h2>
      <DiagramInputShaping />
      <Table2 rows={[
        ['What is ringing?','Ringing (ghosting): ripple pattern on printed surfaces caused by toolhead vibration after direction change\nCaused by: belt elasticity, frame flex, carriage resonance — typical frequency 20–80 Hz\nAppears as: repeated horizontal lines on vertical surfaces, especially after sharp corners\nWorse at higher speeds (higher jerk = larger impulse excitation)'],
        ['ZV shaper','Zero Vibration shaper — simplest input shaper type\nAdds one echo pulse timed to cancel the resonance oscillation\nLow latency (short smoothing window) — good for low-Q (well-damped) systems\nFrequency range: 20–100 Hz; lower max accel than EI but simpler implementation'],
        ['EI shaper','Extra Insane (Extended Insane) — three-pulse shaper\nBetter suppression over a wider frequency band — tolerates more frequency drift\nHigher max acceleration for same print quality compared to ZV\nPreferred for stiff, well-built CoreXY machines (Voron, RatRig)'],
        ['MZV shaper','Modified Zero Vibration — compromise between ZV and EI\nGood vibration suppression with moderate max acceleration penalty\nDefault recommendation in Klipper for most CoreXY machines\nFrequency sensitivity: moderate — tolerates small frequency drift better than ZV'],
        ['ADXL345 calibration','3-axis MEMS accelerometer — $3–5 module from common suppliers\nMounts directly to the toolhead or carriage for X-axis test; to bed for Y-axis test\nKlipper: connect via SPI (4-wire) to Raspberry Pi GPIO\nOutput: frequency spectrum → peak identification → automatic shaper recommendation'],
      ]}/>

      <h2 style={S.h2}>Stepper Motor Sizing &amp; Driver Selection</h2>
      <DiagramMotorSizing />
      <Table2 rows={[
        ['Motor frame size','NEMA17 (42×42 mm): universal standard for desktop CoreXY\nNEMA14 (35×35 mm): used for lighter gantry builds; lower inertia rotor is advantage\nNEMA23 (57×57 mm): large format or high-force applications; usually overkill for CoreXY XY\nThe motor rotor inertia also matters: lighter rotor = faster electrical time constant'],
        ['Step angle choice','1.8° (200 steps/rev): standard — 80 steps/mm with GT2/20T\n0.9° (400 steps/rev): doubles resolution without microstepping overhead; reduces cogging\n0.9° advantage: smoother motion at low speeds, better microstepping accuracy\n0.9° disadvantage: 10–15% lower peak torque; runs slightly hotter at same current'],
        ['TMC2209 driver','UART-configurable: current, microstepping, SpreadCycle/StealthChop mode via firmware\nStallGuard4: sensorless homing — detect motor stall force → use as limit switch\nStealthChop: very quiet at low speed (audible improvement); switches to SpreadCycle at high speed\nCurrent: set RMS via firmware; peak = RMS × 1.41; hold current typically 50% of run current'],
        ['TMC5160 driver','External MOSFETs → handles up to 3A RMS (vs 1.2A for TMC2209)\nDesigned for 24–48V supplies — recommended for high-performance CoreXY\nNo internal temperature limiting at high currents — needs heatsinking on driver board\nUsed in Voron 2.4 Octopus/Spider boards for high-speed builds'],
        ['Current and temperature','Too little current: missed steps, especially during fast direction changes\nToo much current: motor/driver overheating, thermal shutdown, demagnetisation\nSafety check: motor should be warm but not hot to touch (< 60°C at steady state)\nDriver cooling: at ≥1.0 A run current, active cooling (fan or heatsink) strongly recommended'],
        ['48V systems','24V → 48V: doubles back-EMF threshold → motor stays in torque plateau to ~2× RPM\nResult: same NEMA17 delivers full torque at 2× the print speed\nRequires: 48V-rated drivers (TMC5160, TMC5161); 48V power supply; 48V-rated wiring\nRisk: higher voltage → more dangerous wiring; more critical short-circuit protection'],
      ]}/>

      <h2 style={S.h2}>Popular Implementations &amp; Design Choices</h2>
      <DiagramImplementations />
      <Table2 rows={[
        ['Voron 2.4','Open-source, community-designed; sourced from AliExpress + Formbot kit\nFlying gantry: Z-axis gantry hangs from 4 leadscrew corners → QGL (quad gantry levelling)\nAll motion via Klipper on Raspberry Pi; CAN bus toolhead optional\nActive community: Voron Discord, extensive documentation, sourcing guides'],
        ['Voron Trident','Simpler Z than V2.4: 3-point kinematic bed (vs 4-point gantry)\nGantry fixed; bed moves in Z — cleaner than flying gantry for stability\nSmaller build volume than V2.4 at same frame size (bed Z limits max print height)\nPreferred for users who want simpler calibration and maintenance'],
        ['RatRig V-Core 4','Larger format focus (300–500 mm print volume)\nEVA toolhead system: modular carriage accepting multiple extruder/hotend combinations\nBuilt-in frame rigidity design: heavier extrusion profile, fewer printed structural parts\nDedicated Klipper config files; active community on Discord'],
        ['Bambu Lab X1/X1C','Commercial closed-source CoreXY; highest out-of-box performance\nAI monitoring camera: detects spaghetti failures and first-layer issues in real time\nAMS (Automatic Material Station): multi-colour printing without manual filament swap\nLimitation: closed ecosystem, limited firmware customisation, subscription for some AI features'],
        ['Annex Engineering K3','Ultra-lightweight carriage design: CroXY belt geometry variant\nExceptionally high acceleration (50,000+ mm/s² achieved) by minimising all moving mass\nFully parametric design: OpenSCAD source available\nSmall community but influential for pushing limits of CoreXY performance'],
        ['Frame material tradeoffs','Aluminium 2020 extrusion: cheap, light, sufficient for <400 mm volume\n2040/4040 extrusion: stiffer, heavier — used for large format or high-speed builds\nSteel frame: maximum rigidity, complex to build, heavy — industrial applications\nEnclosure: acrylic panels for ABS/ASA (temperature retention); polycarbonate for higher temp'],
      ]}/>

      <h2 style={S.h2}>Calibration Procedure</h2>
      <Table2 rows={[
        ['Step 1 — Frame squareness','Measure both diagonals of the frame; adjust corner brackets until equal to <0.5 mm\nCheck X-axis gantry parallelism to front and rear frame members\nUse machined square or precision angle reference; not printed parts\nRe-check after tightening all fasteners — tightening can disturb squareness'],
        ['Step 2 — Belt tension','Route both belts; clamp at carriage; tension by sliding motor or using tensioner\nMeasure resonant frequency with Gates Carbon Drive app or similar\nTarget: 100–140 Hz for GT2 6mm at ~300 mm free span\nEqual tension both belts: unequal tension → asymmetric X/Y squareness and binding'],
        ['Step 3 — Steps/mm','Calculate from geometry: steps/mm = (steps/rev × microsteps) / (pulley_teeth × belt_pitch)\nFor NEMA17 1.8°, 16× micro, 20T GT2: 80 steps/mm\nVerify: command 100 mm move, measure actual travel with calipers — adjust M92 if needed\nNote: for CoreXY both A and B motors must have identical steps/mm'],
        ['Step 4 — Endstop / homing','X/Y endstops: typically optical, Hall-effect, or sensorless (TMC stallGuard)\nZ endstop: physical switch, BLTouch/CR Touch, Beacon/Eddy (inductive), PINDA probe\nSensorless homing: requires careful sensitivity tuning (SGTHRS/TCOOLTHRS) per motor current and speed\nSafe homing: ensure X homes first before Z probe triggers (prevents bed collision)'],
        ['Step 5 — Z offset & bed levelling','Z offset: distance from nozzle to bed at home position — tune with baby-stepping\nBed mesh (BLTouch/Beacon): maps Z variation across bed surface → compensated in firmware\nQuad gantry levelling (Voron 2.4): probes 4 corners, adjusts Z-leadscrews until gantry level\n3-point kinematic (Trident): 3-screw bed, firmware tilts bed to match gantry plane'],
        ['Step 6 — Input shaping','Mount ADXL345 to toolhead, run TEST_RESONANCES in Klipper\nGenerate frequency plot, identify X and Y peak frequencies\nApply recommended shaper type and frequency in printer.cfg\nRe-run resonance test to verify suppression; print ringing test tower at rated speed'],
        ['Step 7 — Pressure advance','Prints a PA (pressure advance) calibration tower — varies PA value per layer\nFind layer where corner bulging/trailing disappears — that is optimal PA value\nPA depends on: filament type, temperature, print speed, extruder type\nRe-calibrate PA when: changing filament brand/type, temperature, or extruder'],
      ]}/>

      <h2 style={S.h2}>Failure Modes &amp; Troubleshooting</h2>
      <Table2 rows={[
        ['Belt skipping','Symptom: layer shift mid-print, especially after fast direction changes\nCauses: insufficient belt tension, pulley grub screw loose, motor current too low\nDiagnosis: watch carriage during fast move; listen for clicking from motor/pulley\nFix: re-tension belts, secure pulley grub screws with thread locker, increase motor current'],
        ['Ringing / ghosting','Symptom: ripple pattern on vertical surfaces after corners\nCauses: resonance from belt, frame, or bearings — typically 20–80 Hz\nDiagnosis: print ringing test object at increasing speeds; identify onset speed\nFix: tune input shaping (ADXL345 + Klipper); reduce acceleration; check belt tension'],
        ['X/Y squareness error','Symptom: printed square is a parallelogram; rectangles have unequal diagonals\nCauses: frame not square, belt path asymmetric, different steps/mm A vs B\nDiagnosis: print 200mm square, measure diagonals — error = 2× frame skew\nFix: [skew_correction] in Klipper; physically square frame if error >1 mm'],
        ['Bearing noise / stiction','Symptom: grinding/buzzing during moves, vibration felt through frame\nCauses: over-preloaded MGN carriage, contamination, incorrect lubrication\nDiagnosis: move carriage by hand with power off — should glide smoothly\nFix: clean rails with IPA, re-lubricate with Superlube or light machine oil; replace worn bearings'],
        ['Thermal expansion drift','Symptom: Z offset changes between cold and hot prints; first layer inconsistent\nCauses: aluminium frame expands with enclosure temperature (23 μm/m/°C)\nFor 300 mm frame: 0.007 mm per °C → significant for enclosed high-temp builds\nFix: home Z when at print temperature; use Beacon/Eddy for live Z compensation'],
        ['Motor skipping at speed','Symptom: motor makes grinding sound at high speed; carriage stutters\nCauses: motor current too low for required torque at speed, 24V torque rolloff\nDiagnosis: check motor temperature (should be warm not hot); increase current gradually\nFix: increase run current; consider 48V PSU and TMC5160 drivers; use 0.9° motor for lower resonance'],
      ]}/>

      <InfoBox title="CoreXY Key Equations &amp; Numbers" items={[
        "Inverse kinematics:  A_steps = X + Y   ·   B_steps = X − Y",
        "Forward kinematics:  X = (A + B) / 2   ·   Y = (A − B) / 2",
        "Steps per mm (GT2, 20T, NEMA17 1.8°, 16× micro):  (200 × 16) / (20 × 2) = 80 steps/mm",
        "Steps per mm (GT2, 20T, NEMA17 0.9°, 16× micro):  (400 × 16) / (20 × 2) = 160 steps/mm",
        "Belt velocity at 300 mm/s, 20T GT2:  300 mm/s → motor at  300/40 × 60 = 450 RPM",
        "Belt tension target:  40–60 N per belt strand  (measure via resonant frequency)",
        "Belt frequency (GT2, 6mm, 300 mm span, 50 N):  f ≈ 117 Hz  (Gates formula)",
        "Peak diagonal speed:  v_motor_max = v_axis_max × √2  (45° move uses one motor at full speed)",
        "Frame thermal expansion (Al):  23 μm/m/°C  →  300 mm frame moves 7 μm per °C",
        "Typical resonance range:  20–80 Hz for desktop CoreXY; lower = softer belts/frame",
        "Input shaper latency (ZV):  T/2 where T = 1/f_resonance  (e.g. 45 Hz → 11 ms smoothing window)",
        "NEMA17 rated current (typical):  1.7–2.0 A peak  ·  Run at 70–90% = 1.2–1.8 A peak",
      ]}/>
    </div>
  );
}